import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { AIHealth, AIHealthCounts } from "../api/types";
import { ShellIcon } from "./ShellIcons";

/**
 * 连线: whether 小爱 is answering customers, and on the model she was given
 * or on the stand-in.
 *
 * Owner, 2026-10-10: 「我想要一个提醒或展示能看见ai连接状态」. A week had sent 23
 * of 200 replies to 2.5 Flash because 3.8 Flash was slow or failing on
 * Google's side, and nothing on any page said so.
 */

/** Read again every five minutes, on the AI page and for the dot on its tab. */
const EVERY = 5 * 60_000;

export function useAIHealth(enabled: boolean): AIHealth | null {
  const [h, setH] = useState<AIHealth | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const read = () =>
      api
        .aiHealth()
        .then((x) => live && setH(x))
        .catch(() => undefined); // keep the last reading; the card is a hint, not a gate
    void read();
    const id = window.setInterval(() => void read(), EVERY);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [enabled]);
  return h;
}

/** "gemini-3.8-flash" → "3.8 Flash"; "gemini-3.1-pro-preview" → "3.1 Pro" (as production 5dbdead; api console modelShort). */
export function modelName(m: string): string {
  if (!m) return "";
  const x = /^gemini-([\d.]+)-flash(-lite)?$/.exec(m);
  if (x) return `${x[1]} Flash${x[2] ? " Lite" : ""}`;
  const p = /^gemini-([\d.]+)-pro(-preview)?$/.exec(m);
  return p ? `${p[1]} Pro` : m;
}

const sec = (ms: number) => (ms >= 1000 ? `${Math.round(ms / 1000)} 秒` : "不到 1 秒");

/** 「，一般 7 秒」, or nothing: calls before 10/10 18:42 were never timed and
 *  read 0, which is not 「不到 1 秒」. */
const typical = (c: AIHealthCounts) => (c.p50_ms > 0 ? `，一般 ${sec(c.p50_ms)}` : "");

/** ok and quiet need nothing; slow and backup want a look; down is urgent. */
export function healthTone(h: AIHealth | null): "" | "warn" | "bad" {
  if (!h) return "";
  if (h.status === "down") return "bad";
  if (h.status === "backup" || h.status === "slow") return "warn";
  return "";
}

/** The stand-ins that answered in c, by name, most first (production 984898b). A reply has two since 10/23, 3.1 Pro
 *  and then 2.5 Flash, and naming only the configured one read a 3.1 Pro answer as 2.5 Flash (owner, 10/24:
 *  「3.1pro上线了？…怎么还是只有3.8flash和2.5flash?」). An API from before sends no backups: its one stand-in stands
 *  for all of them. The order they stand in is the server's, and not copied here. */
function standIns(c: AIHealthCounts, fallback: string): [string, number][] {
  const b = c.backups;
  if (!b || Object.keys(b).length === 0) return c.on_backup > 0 ? [[fallback, c.on_backup]] : [];
  return Object.entries(b)
    .map(([m, n]): [string, number] => [modelName(m), n])
    .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]));
}

/** 「3.1 Pro、2.5 Flash」: the stand-ins' names. */
const standInNames = (c: AIHealthCounts, fallback: string, sep = "、") =>
  standIns(c, fallback)
    .map(([name]) => name)
    .join(sep) || fallback;

/** 「（3.1 Pro 没接上）」 when every stand-in answer in c came from further down the line than its first: a 2.5 Flash answer
 *  is asked only after 3.1 Pro failed or was not back in 40 s. The pill said 「备用在回 · 2.5 Flash」 for such an hour and the
 *  owner took the stand-in to have been changed (2026-10-30: 「为什么现在备用model是2.5flash? 3.1pro连接不到？」). */
function skipped(h: AIHealth, c: AIHealthCounts): string {
  const first = h.stand_ins?.[0];
  const b = c.backups;
  if (!first || !b || Object.keys(b).length === 0 || (b[first] ?? 0) > 0) return "";
  return `（${modelName(first)} 没接上）`;
}

/** 「3.1 Pro → 2.5 Flash → 3.6 Flash」: who stands in for a reply, in order; the most frequent stand-in from an older API. */
export function standInLine(h: AIHealth): string {
  if (h.stand_ins && h.stand_ins.length > 0) return h.stand_ins.map(modelName).join(" → ");
  return modelName(h.backup_model);
}

function split(c: AIHealthCounts, main: string, backup: string): string {
  if (c.on_backup === 0) return `都是 ${main}`;
  const b = standIns(c, backup).map(([name, n]) => `备用 ${name} ${n} 句`);
  return `${main} ${c.on_chosen} 句、${b.join("、")}`;
}

/** The card's one line. */
export function healthLine(h: AIHealth): string {
  const c = h.last_hour;
  const main = modelName(h.chosen_model) || "选的模型";
  const backup = modelName(h.backup_model) || "备用模型";
  switch (h.status) {
    case "quiet":
      return "过去 1 小时没有客户要小爱回";
    case "down":
      return `小爱回不了客户：过去 1 小时 ${c.failed + c.gave_up} 次没回成`;
    case "backup":
      return `${main} 连不上，现在由备用 ${standInNames(c, backup)} 在回${skipped(h, c)}（过去 1 小时 ${c.on_backup} 句）`;
    case "slow":
      return `有点慢：过去 1 小时回了 ${c.answered} 句，${split(c, main, backup)}${typical(c)}`;
    default:
      return `正常：过去 1 小时回了 ${c.answered} 句，${split(c, main, backup)}${typical(c)}`;
  }
}

/**
 * The line under 小爱 in the menu (prototype: 「值守中 · 平均 20 秒回」): whether
 * she is on, and how fast she answered in the last hour — the median, so one
 * slow reply does not speak for the hour. Short enough for the sidebar.
 */
export function healthShort(h: AIHealth): string {
  // The time she takes to write a line: not how long a customer waits for the first (the page's 第一句 tile).
  const fast = h.last_hour.p50_ms > 0 ? ` · 写一句 ${sec(h.last_hour.p50_ms)}` : "";
  switch (h.status) {
    case "down":
      return "回不了客户";
    case "backup":
      return "备用模型在回";
    case "slow":
      return `有点慢${fast}`;
    case "quiet":
      return "值守中";
    default:
      return `值守中${fast}`;
  }
}

/** The pill's words, the prototype's (v2.20 AiHero): the model, then how it is doing, 「3.8 Flash 有点慢」. It was the
 *  other way round for a while (10/24); the hour-by-hour line under the pointer says the rest. */
function pillWords(h: AIHealth): string {
  const main = modelName(h.chosen_model);
  const backup = modelName(h.backup_model) || "备用模型";
  switch (h.status) {
    case "quiet":
      return "这 1 小时没客户来";
    case "down":
      return "回不了客户 · 请同事先接";
    case "backup":
      return `备用在回 · ${standInNames(h.last_hour, backup, "／")}${skipped(h, h.last_hour)}`;
    case "slow":
      return main ? `${main} 有点慢` : "有点慢";
    default:
      return main ? `${main} 正常` : "正常";
  }
}

/**
 * 连线, beside the AI page's title: the model she is on and whether it is
 * answering; a press opens the last 24 hours under the title.
 *
 * Owner, 2026-10-10, twice: first 「我想要一个提醒或展示能看见ai连接状态」, then of
 * the three tiles that had grown under the title (the version, this, last
 * week's conversion) 「展示了看不懂的数据 不要为了展示而展示」. What is left is the
 * one thing nothing else on the page says: is she answering, and on what.
 */
export function HealthPill({ health, open, onToggle }: { health: AIHealth | null; open: boolean; onToggle: () => void }) {
  if (!health) return null;
  const tone = healthTone(health);
  const quiet = health.status === "quiet";
  return (
    <button
      type="button"
      className={`ai-conn${tone ? " " + tone : ""}${quiet ? " quiet" : ""}`}
      onClick={onToggle}
      aria-expanded={open}
      title={healthLine(health)}
    >
      <i className="ai-health-dot" aria-hidden="true" />
      <span>{pillWords(health)}</span>
      <ShellIcon name="down" size={14} className={open ? "ai-conn-up" : undefined} />
    </button>
  );
}

/** One hour's colour: red when a customer went unanswered all hour, amber
 *  when the stand-in wrote some of it, green when the chosen model wrote all
 *  of it, grey when nobody wrote in. A failed ask that was asked again and
 *  answered is not the customer's problem, so it does not colour the hour. */
type HourState = "none" | "ok" | "backup" | "down";
const hourState = (c?: AIHealthCounts): HourState =>
  !c || c.calls === 0 ? "none" : c.answered === 0 ? "down" : c.on_backup > 0 ? "backup" : "ok";

/** 「回了 8 句：6 句 3.8 Flash、1 句备用 3.1 Pro、1 句备用 2.5 Flash」. */
function sentence(c: AIHealthCounts, main: string, backup: string): string {
  if (c.calls === 0) return "没有客户要小爱回";
  if (c.answered === 0) return `回不了：问了 ${c.calls} 次都没回成`;
  if (c.on_backup === 0) return `回了 ${c.answered} 句，都是 ${main}`;
  const b = standIns(c, backup).map(([name, n]) => `${n} 句备用 ${name}`);
  return `回了 ${c.answered} 句：${c.on_chosen} 句 ${main}、${b.join("、")}`;
}

/** The last 24 hours as a strip, an hour a cell, Malaysian time: which model
 *  answered when — 「什么时间连接什么ai」. A press on an hour reads it out. */
export function HealthDay({ health }: { health: AIHealth }) {
  const [at, setAt] = useState<string | null>(null);
  const main = modelName(health.chosen_model) || "选的模型";
  const backup = modelName(health.backup_model) || "备用模型";
  const byHour = new Map(health.hours.map((x) => [x.hour, x]));
  // The hour keys the server uses: "2026-10-10T17", Malaysia's clock.
  const now = Date.now();
  const cells = Array.from({ length: 24 }, (_, i) => {
    const t = new Date(now - (23 - i) * 36e5 + 8 * 36e5).toISOString();
    return { key: t.slice(0, 13), hour: Number(t.slice(11, 13)) };
  });
  const picked = at ? byHour.get(at) : undefined;
  const pickedHour = at ? Number(at.slice(11, 13)) : 0;
  return (
    <div className="ai-day">
      <div className="ai-day-strip" role="group" aria-label="过去 24 小时，每格一小时">
        {cells.map((c, i) => {
          const st = hourState(byHour.get(c.key));
          return (
            <button
              key={c.key}
              type="button"
              // its place in the strip: the cells grow in one after another (xa.css, 18 ms apart)
              style={{ ["--i" as string]: i }}
              className={`ai-day-cell ${st}${at === c.key ? " on" : ""}`}
              onClick={() => setAt(at === c.key ? null : c.key)}
              title={`${c.hour}:00 ${sentence(byHour.get(c.key) ?? EMPTY, main, backup)}`}
              aria-label={`${c.hour} 点`}
            />
          );
        })}
      </div>
      <div className="ai-day-axis" aria-hidden="true">
        {cells.map((c, i) => (
          <span key={c.key}>{i === 23 ? "现在" : c.hour % 6 === 0 ? `${c.hour} 点` : ""}</span>
        ))}
      </div>
      <p className="ai-day-line">
        {at ? (
          <>
            <b>
              {pickedHour}:00–{(pickedHour + 1) % 24}:00
            </b>{" "}
            {sentence(picked ?? EMPTY, main, backup)}
          </>
        ) : (
          <>
            <b>过去 24 小时</b> {sentence(health.last_day, main, backup)}
          </>
        )}
      </p>
      <ul className="ai-day-key">
        <li>
          <i className="ai-day-cell ok" />
          {modelName(health.chosen_model) ? `${modelName(health.chosen_model)}（平常用）` : "小爱回的"}
        </li>
        <li>
          <i className="ai-day-cell backup" />
          {health.backup_model || health.last_day.on_backup > 0 ? `有备用回的（${standInNames(health.last_day, backup, "／")}）` : "有备用模型回的"}
        </li>
        <li>
          <i className="ai-day-cell down" />
          回不了
        </li>
        <li>
          <i className="ai-day-cell none" />
          没客户
        </li>
      </ul>
    </div>
  );
}

const EMPTY: AIHealthCounts = { answered: 0, on_chosen: 0, on_backup: 0, failed: 0, gave_up: 0, extra: 0, calls: 0, p50_ms: 0, p95_ms: 0 };
